/* ---------------------------------------------------------------------------
   The application header.

   Two zones. On the left a brand block exactly as wide as the nav pane beneath
   it, so the seam between navigation and content runs unbroken from the top of
   the window to the bottom. On the right the things that are true of the whole
   app rather than of one screen: what you are searching for, where the register
   on screen came from, and who Cloudflare Access says you are.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, store, type State } from '../core/store.ts';
import { appById, appForShell, owns } from '../core/apps.ts';

import { allBridges } from '../data/derive.ts';
import { visibleRegister } from '../screens/register.ts';
import { backendPill, queuePill } from './statusbar.ts';
import { flyout, menuButton, menuItem, menuLabel, menuSection, menuSep } from './flyout.ts';
import { LOCALE_NAMES, type Locale } from '../data/locale.ts';
import { ROLE_COPY, rolesLabel } from '../data/roles.ts';
import { agency, brand } from '../tenant/agency.ts';
import { active } from '../tenant/registry.ts';

/**
 * The language control.
 *
 * It says how much of the interface exists in the language it is offering, and
 * that is the point rather than a detail: the Nepali catalogue covers the
 * navigation, the screen titles and the summary report, and nothing else. A
 * control that simply said "नेपाली" would be promising a translated application
 * and delivering a translated menu. See the header of src/data/locale.ts.
 */
function localeSwitch(s: Readonly<State>): HTMLElement | null {
  /* NO CONTROL WHERE THERE IS NO CHOICE. This mapped the global LOCALES list
     until there was a second department, so a DPWH session was offered नेपाली —
     a language that department does not work in, over a catalogue written for
     Nepal's inventory sheet. Each tenant declares what it offers (`locales` in
     src/tenant/types.ts) and a tenant offering one language gets no control at
     all, rather than a control with one button in it. */
  const offered = active().locales;
  if (offered.length < 2) return null;

  return h(
    'div',
    { class: 'tb-lang', role: 'group', 'aria-label': 'Language' },
    ...offered.map((code: Locale) => {
      const on = s.locale === code;
      return h(
        'button',
        {
          type: 'button',
          class: ['tb-lang__btn', on && 'tb-lang__btn--on'],
          'aria-pressed': on ? 'true' : 'false',
          title:
            code === 'en'
              ? 'English \u2014 every screen'
              : 'Nepali \u2014 navigation, screen titles and the network summary. The inventory fields stay in English because the DoR sheet prints them in English, and the provenance notes stay in English because a mistranslated caveat is worse than none.',
          onclick: () => actions.localeSelected(code),
        },
        LOCALE_NAMES[code],
      );
    }),
  );
}

/** Initials for the avatar — "s.bhattarai@dor.gov.np" -> "SB". */
function initials(identity: State['identity']): string {
  if (!identity) return '—';
  const source = identity.email ?? identity.serviceToken ?? '';
  const local = source.split('@')[0] ?? '';
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  const letters = (parts.length > 1 ? [parts[0]!, parts[1]!] : [local]).map((p) => p[0] ?? '');
  return letters.join('').toUpperCase().slice(0, 2) || '—';
}

/**
 * What to print on the second line, and what to say about it on hover.
 *
 * THE ROLE GOES HERE, in the space that used to say "Signed in". Signed in was
 * true and useless — the name above it says the same thing — and the question
 * this corner is actually asked is why a button somebody remembers is not there
 * any more. The answer is the role, so the role is what it prints.
 *
 * Null where the deployment does not enforce Access, and the old wording is kept
 * for that case: there is no role because there is nobody to have one.
 */
function roleLine(s: Readonly<State>): { text: string; note: string } {
  const identity = s.identity;
  if (!identity?.roles) {
    return {
      text: 'Signed in',
      note:
        'This deployment does not enforce roles, so every action is permitted. /api/health says so too.',
    };
  }

  const text = rolesLabel(identity.roles, identity.suspended);
  return {
    text,
    note: identity.suspended
      ? 'This account is suspended: it may do nothing here, including read.'
      : identity.roles.length === 0
        ? 'Nobody has granted you a role, so you read the register and write nothing to it.'
        : identity.roles.map((role) => `${ROLE_COPY[role].label} — ${ROLE_COPY[role].who}`).join(' ') +
          (identity.roleSource === 'deployment'
            ? ' Administrator from this deployment’s own administrator list.'
            : ''),
  };
}

/** Who, and what they may do: the name beside the avatar, the role in the account menu and the tooltip. */
function whoLines(s: Readonly<State>): { name: string; role: string; title: string } {
  if (s.identity?.email) {
    const role = roleLine(s);
    return {
      name: s.identity.email.split('@')[0]!.replace(/[._]/g, ' '),
      role: role.text,
      title:
        `Signed in as ${s.identity.email} — every edit is recorded against this address. ` +
        role.note,
    };
  }
  if (s.identity?.serviceToken) {
    return {
      name: 'Service token',
      role: s.identity.roles
        ? rolesLabel(s.identity.roles, s.identity.suspended)
        : s.identity.serviceToken.slice(0, 12),
      title:
        `Authenticated by service token ${s.identity.serviceToken}. ` + roleLine(s).note,
    };
  }
  if (s.backend === 'live') {
    return {
      name: 'Not identified',
      role: 'Access off',
      title:
        'This deployment does not enforce Cloudflare Access, so the backend cannot say who is signed in. Edits are recorded as "unknown".',
    };
  }
  return { name: 'Not identified', role: 'Offline', title: 'The backend has not been reached yet.' };
}

/**
 * The one search in the app.
 *
 * It searches the register, so typing in it means "find me structures" and takes
 * you there. That is the whole reason it is in the header rather than above the
 * table: a search you have to navigate to first is one you use less.
 */
function search(s: Readonly<State>): HTMLElement {
  const shown = visibleRegister().length;
  const total = allBridges().length;

  return h(
    'label',
    { class: 'hsearch' },
    icon(ICONS.search, { size: 15 }),
    h('input', {
      id: 'header-search',
      type: 'search',
      value: s.q,
      placeholder: 'Search asset id, structure, river, road… (Alt+Q)',
      'aria-label': 'Search the register',
      oninput: (e: Event) => {
        const q = (e.target as HTMLInputElement).value;
        /* Typing here means "find me structures", so it takes you to the list —
           but only in an application that HAS one. On the home page the same
           search filters the table already on screen, and a jump to a screen
           this document does not draw would have left a blank page under a
           search box. */
        const app = appForShell(s.shell);
        const typed = q.trim() !== '';
        /* On a desk the register is the start page (see deskSendsHome), so a
           search typed inside an application goes home to it. */
        if (typed && s.shell === 'desk' && app.id !== 'home') {
          actions.openApp('home', 'dashboard');
          store.set({ q });
          return;
        }
        const jumps = typed && s.screen !== 'list' && owns(app, 'list');
        store.set(jumps ? { q, screen: 'list' } : { q });
      },
    }),
    h('span', { class: 'hsearch__count', text: `${shown} of ${total}` }),
  );
}

export function titlebar(s: Readonly<State>): HTMLDivElement {
  const who = whoLines(s);
  const app = appForShell(s.shell);
  const home = app.id === 'home';

  return h(
    'div',
    { class: 'titlebar' },

    h(
      'div',
      { class: 'titlebar__brand' },
      /* THE WAY BACK TO THE START PAGE, where a WinUI window keeps its back
         button. The ribbon's File tab goes to the same place; this is the one a
         hand reaches for without reading. */
      home
        ? null
        : h(
            'a',
            {
              class: 'tb-btn tb-btn--icon titlebar__back',
              href: '#/',
              title: 'Back to the start page',
              'aria-label': 'Back to the start page',
            },
            icon(ICONS.arrowLeft, { size: 16 }),
          ),
      brand().logo
        ? h(
            'span',
            { class: 'titlebar__mark titlebar__mark--logo' },
            h('img', { src: brand().logo, alt: agency().departmentShort }),
          )
        : h('span', { class: 'titlebar__mark' }, icon(ICONS.bridge, { size: 16, stroke: 'var(--on-accent)' })),
      h(
        'div',
        { class: 'titlebar__names' },
        /* The product, then what you are in: the app on the app page, the
           department on the start page. A long agency name is truncated by the
           CSS and read whole from `title`. */
        h('span', { class: 'titlebar__title', text: appById('home').brand }),
        h('span', {
          class: 'titlebar__sub',
          text: home ? `${agency().departmentShort} · ${agency().branch}` : app.brand,
          title: home ? `${agency().department} — ${agency().branch}` : app.blurb,
        }),
      ),
    ),

    /* --- rail --- */
    h(
      'div',
      { class: 'titlebar__rail' },
      search(s),
      h('span', { class: 'spacer' }),
      queuePill(s),
      backendPill(s),
      localeSwitch(s),

      /* Help and Settings, where a WinUI title bar keeps them. Help opens the
         guide docked on the right of the app you are in (on the field shell,
         which has no room beside the screen, it is the guide screen); Settings
         is Options, which says who you are signed in as and is where signing
         out is. */
      h(
        'button',
        {
          type: 'button',
          class: ['tb-btn', 'tb-btn--icon', s.guideOpen && s.shell !== 'field' && 'tb-btn--on'],
          title: 'Guide',
          'aria-label': 'Guide',
          'aria-pressed': s.shell !== 'field' ? String(s.guideOpen) : undefined,
          onclick: () =>
            s.shell !== 'field'
              ? actions.toggleGuide()
              : owns(app, 'guide')
                ? actions.go('guide')
                : actions.openApp('home', 'guide'),
        },
        icon(ICONS.help, { size: 16 }),
      ),
      h(
        'button',
        {
          type: 'button',
          class: 'tb-btn tb-btn--icon',
          title: 'Settings — this device, your role, and signing out',
          'aria-label': 'Settings',
          ...menuButton(s, 'settings'),
        },
        icon(ICONS.settings, { size: 16 }),
      ),

      h(
        'button',
        {
          type: 'button',
          class: 'tb-btn tb-btn--icon',
          title: s.theme === 'light' ? 'Switch to dark' : 'Switch to light',
          'aria-label': 'Toggle light and dark theme',
          onclick: () => actions.toggleTheme(),
        },
        icon(s.theme === 'light' ? ICONS.moon : ICONS.sun, { size: 15 }),
      ),

      /* THE DESK SHELL'S WAY TO ITS OWN PROFILE. Options is not in the nav pane —
         it is the field shell's third tab — so the corner that names you is
         where a desk user presses for who they are signed in as, their role, the
         shell they are in and signing out. A menu now, because the redesign put
         five destinations behind it, and flyout.ts gives every menu the same
         dismiss rule. */
      h(
        'button',
        {
          type: 'button',
          class: 'whoami whoami--btn',
          title: who.title,
          'aria-label': `Account — ${who.name}`,
          ...menuButton(s, 'account'),
        },
        h('span', { class: 'whoami__avatar', text: initials(s.identity) }),
        h(
          'div',
          { style: 'min-width:0' },
          h('div', { class: 'whoami__name ellipsis', text: who.name }),
        ),
      ),
    ),

    accountMenu(s, who),
    settingsMenu(s),
  );
}

/* ------------------------------ the two menus ------------------------------ */

function accountMenu(s: Readonly<State>, who: { name: string; role: string }): HTMLElement | null {
  return flyout(
    s,
    'account',
    { label: 'Account', width: 288, align: 'end' },
    menuSection(
      h(
        'div',
        { class: 'flyout__who' },
        h('span', { class: 'whoami__avatar flyout__avatar', text: initials(s.identity) }),
        h(
          'div',
          { style: 'min-width:0' },
          h('div', { class: 'flyout__name ellipsis', text: who.name }),
          h('div', { class: 'flyout__sub', text: who.role }),
          h('div', { class: 'flyout__sub num', text: agency().department }),
        ),
      ),
    ),
    h(
      'div',
      { class: 'flyout__list' },
      menuItem({
        label: 'Profile',
        sub: s.identity?.email ?? 'Who you are signed in as, and your role',
        iconPath: ICONS.user,
        onClick: () => actions.openApp('inspection', 'options'),
      }),
      menuItem({
        label: 'Change your password',
        iconPath: ICONS.key,
        onClick: () => actions.openApp('inspection', 'options'),
      }),
      menuItem({ label: 'Settings', iconPath: ICONS.settings, onClick: () => actions.toggleMenu('settings') }),
      menuSep(),
      menuItem({
        label: 'Desktop',
        sub: 'The full register, with every screen',
        iconPath: ICONS.monitor,
        checked: s.shell === 'desk',
        onClick: () => actions.shellChosen('desk'),
      }),
      menuItem({
        label: 'Field',
        sub: 'Three tabs, for use at a structure',
        iconPath: ICONS.phone,
        checked: s.shell === 'field',
        onClick: () => actions.shellChosen('field'),
      }),
      s.identity
        ? [menuSep(), menuItem({ label: 'Sign out', iconPath: ICONS.logout, danger: true, onClick: () => void actions.signOut() })]
        : null,
    ),
  );
}

function settingsMenu(s: Readonly<State>): HTMLElement | null {
  const waiting = s.queued.length;
  const locales = active().locales;
  const fact = (k: string, v: string) =>
    h('div', { class: 'flyout__fact' }, h('span', { text: k }), h('span', { class: 'num', text: v }));

  return flyout(
    s,
    'settings',
    { label: 'Settings', width: 336, align: 'end' },
    menuSection(
      h('div', { class: 'flyout__name', text: 'Settings' }),
      h('div', { class: 'flyout__sub', text: 'This device, your role, and signing out' }),
    ),
    h(
      'div',
      { class: 'flyout__list' },
      menuLabel('Theme'),
      menuItem({ label: 'Light', iconPath: ICONS.sun, checked: s.theme === 'light', onClick: () => actions.setTheme('light') }),
      menuItem({ label: 'Dark', iconPath: ICONS.moon, checked: s.theme === 'dark', onClick: () => actions.setTheme('dark') }),
      locales.length > 1
        ? [
            menuLabel('Language'),
            ...locales.map((code: Locale) =>
              menuItem({
                label: LOCALE_NAMES[code],
                iconPath: ICONS.language,
                checked: s.locale === code,
                onClick: () => actions.localeSelected(code),
              }),
            ),
          ]
        : null,
      menuSep(),
      menuLabel('Data & sync'),
    ),
    menuSection(
      fact('Connection', s.backend === 'live' ? 'Reachable' : s.backendNote || 'Unreachable'),
      fact('Waiting to sync', waiting === 0 ? 'Nothing' : String(waiting)),
      s.syncNote ? fact('Last sync', s.syncNote) : null,
    ),
    h(
      'div',
      { class: 'flyout__list' },
      waiting
        ? menuItem({
            label: s.syncing ? 'Sending…' : `Send ${waiting} now`,
            iconPath: ICONS.refresh,
            disabled: s.syncing || s.backend !== 'live',
            onClick: () => void actions.syncQueue(),
          })
        : null,
      s.canInstall
        ? menuItem({ label: 'Install on this device', iconPath: ICONS.install, onClick: () => void actions.promptInstall() })
        : null,
      menuSep(),
      menuItem({ label: 'All settings', iconPath: ICONS.settings, onClick: () => actions.openApp('inspection', 'options') }),
      menuItem({
        label: 'Help & support',
        iconPath: ICONS.help,
        onClick: () => (s.shell !== 'field' ? actions.toggleGuide() : actions.openApp('home', 'guide')),
      }),
    ),
  );
}
